import { ToolUIPart, DynamicToolUIPart } from 'ai';
import { isNil } from 'ramda';
import { ReactNode } from 'react';

import { ToolComponentMap, TOOLS } from '../../tools';
import { GenericTool } from '../../tools/GenericTool';

export type ChatMessageToolPartProps = {
  part: ToolUIPart<TOOLS> | DynamicToolUIPart;
};

/**
 * Render a tool part of a chat message.
 *
 * Not memoized — the AI SDK mutates tool part objects in-place (e.g. state
 * transitions from "input-available" → "output-available") and then deep-clones
 * the message via structuredClone. A custom memo comparator that only checks
 * `toolCallId` and `state` was previously used here but caused the tool UI to
 * miss state transitions, keeping the loading spinner visible indefinitely.
 * Since tool components are lightweight, the re-render cost is negligible.
 */
export const ChatMessageToolPart = ({ part }: ChatMessageToolPartProps): ReactNode => {
  const toolCallId = part.toolCallId;

  // Get the component representing this tool part
  // All tool have a prefix of "tool-" -> 5 characters
  const toolName = part.type.slice(5) as keyof TOOLS;
  const ToolComponent = ToolComponentMap[toolName];

  return isNil(ToolComponent) ? (
    <GenericTool key={`tool-call-${toolCallId}`} tool={part} />
  ) : (
    <ToolComponent key={`tool-call-${toolCallId}`} tool={part} toolName={toolName} />
  );
};
